import type {
    AttachmentBlock,
    AudioBlock,
    LomdaV3Asset,
    LomdaV3AssetType,
    LomdaV3Block,
    LomdaV3BlockType,
    LomdaV3Content,
    MediaCell,
    MediaKind,
    MediaLayoutBlock,
} from "@/lib/utils/lomdaV3/types";
import {
    createAttachmentBlock,
    createAudioBlock,
    createMediaLayout,
    emptyMediaCell,
    variantLabelKey,
} from "./blockRegistry";
import { isCustomIcon } from "./tabIcons";
import { stripHtml } from "./titleHtml";

export interface LomdaMediaUsage {
    pageId: string;
    blockId: string;
    blockType: LomdaV3BlockType;
    variantLabelKey?: string;
}

interface MediaFile {
    type: LomdaV3AssetType;
    // Empty for storage videos, which are known by `videoId`.
    url: string;
    videoId?: string;
    thumbnailUrl?: string;
    name: string;
    size?: number;
}

type MediaRef = MediaFile & LomdaMediaUsage;

export type LomdaLibraryItem = MediaFile & {
    key: string;
    isIcon: boolean;
    usages: LomdaMediaUsage[];
};

const image = (url: string | undefined, name = ""): MediaFile[] =>
    url ? [{ type: "image", url, name }] : [];

const icon = (value: string | undefined): MediaFile[] =>
    value && isCustomIcon(value) ? image(value) : [];

function videoCell(cell: MediaCell): MediaFile[] {
    const name = cell.name || cell.alt;
    if (cell.videoId)
        return [
            {
                type: "video",
                url: "",
                videoId: cell.videoId,
                thumbnailUrl: cell.thumbnailUrl || undefined,
                name,
            },
        ];
    return cell.url ? [{ type: "video", url: cell.url, name }] : [];
}

// Thumbnails (a video's poster, a link card's thumbnail) are not library media.
function refsOfBlock(block: LomdaV3Block): MediaFile[] {
    switch (block.type) {
        case "text": {
            const band = image(block.background?.imageUrl);
            return block.data.variant === "quote"
                ? [...band, ...image(block.data.authorPhoto)]
                : band;
        }
        case "media-layout":
            return block.data.cells.flatMap((cell) =>
                block.data.kind === "video"
                    ? videoCell(cell)
                    : image(cell.url, cell.alt)
            );
        case "audio":
            return block.data.url
                ? [{ type: "audio", url: block.data.url, name: block.data.name }]
                : [];
        case "attachment":
            return block.data.url
                ? [
                      {
                          type: "file",
                          url: block.data.url,
                          name: block.data.name,
                          size: block.data.size || undefined,
                      },
                  ]
                : [];
        case "hotspot":
            return image(block.data.image, block.data.alt);
        case "how-to":
            return block.data.steps.flatMap((s) =>
                image(s.image, stripHtml(s.title))
            );
        case "walkthrough":
            return block.data.steps.flatMap((s) =>
                s.type === "image"
                    ? image(s.image, stripHtml(s.heading || s.name || ""))
                    : []
            );
        case "flip-cards":
            return block.data.cards.flatMap((c) => [
                ...icon(c.icon),
                ...image(c.appearance?.backgroundImage),
            ]);
        case "tabs":
            return block.data.items.flatMap((i) => icon(i.icon));
        default:
            return [];
    }
}

/** A file to delete: its URL, or a storage video's id. */
export type MediaKey = Pick<LomdaLibraryItem, "url" | "videoId">;

const mapChanged = <T>(list: T[], fn: (item: T) => T): T[] => {
    const next = list.map(fn);
    return next.some((item, i) => item !== list[i]) ? next : list;
};

/** `block` with `file` cleared from every field `refsOfBlock` reads. Each
 *  field is left the way that block's own Remove leaves it. */
export function withoutMedia(block: LomdaV3Block, file: MediaKey): LomdaV3Block {
    const hit = (url: string | undefined) => !!file.url && url === file.url;
    switch (block.type) {
        case "text": {
            let next = block;
            if (hit(next.background?.imageUrl))
                next = {
                    ...next,
                    background: { ...next.background, imageUrl: undefined },
                };
            if (next.data.variant === "quote" && hit(next.data.authorPhoto))
                next = { ...next, data: { ...next.data, authorPhoto: "" } };
            return next;
        }
        case "media-layout": {
            const cells = mapChanged(block.data.cells, (cell) =>
                hit(cell.url) ||
                (!!file.videoId && cell.videoId === file.videoId)
                    ? {
                          ...emptyMediaCell(),
                          heading: cell.heading,
                          text: cell.text,
                      }
                    : cell
            );
            return cells === block.data.cells
                ? block
                : { ...block, data: { ...block.data, cells } };
        }
        case "audio":
            return hit(block.data.url)
                ? { ...block, data: { ...block.data, url: "", name: "" } }
                : block;
        case "attachment":
            return hit(block.data.url)
                ? {
                      ...block,
                      data: { ...block.data, url: "", name: "", size: 0 },
                  }
                : block;
        case "hotspot":
            return hit(block.data.image)
                ? { ...block, data: { ...block.data, image: "", markers: [] } }
                : block;
        case "how-to": {
            const steps = mapChanged(block.data.steps, (s) =>
                hit(s.image) ? { ...s, image: "" } : s
            );
            return steps === block.data.steps
                ? block
                : { ...block, data: { ...block.data, steps } };
        }
        case "walkthrough": {
            const steps = mapChanged(block.data.steps, (s) =>
                s.type === "image" && hit(s.image)
                    ? { ...s, image: "", hotspots: [] }
                    : s
            );
            return steps === block.data.steps
                ? block
                : { ...block, data: { ...block.data, steps } };
        }
        case "flip-cards": {
            const cards = mapChanged(block.data.cards, (c) => {
                let next = c;
                if (hit(next.icon)) next = { ...next, icon: "" };
                if (hit(next.appearance?.backgroundImage))
                    next = {
                        ...next,
                        appearance: {
                            ...next.appearance,
                            backgroundImage: undefined,
                        },
                    };
                return next;
            });
            return cards === block.data.cards
                ? block
                : { ...block, data: { ...block.data, cards } };
        }
        case "tabs": {
            const items = mapChanged(block.data.items, (i) =>
                hit(i.icon) ? { ...i, icon: "" } : i
            );
            return items === block.data.items
                ? block
                : { ...block, data: { ...block.data, items } };
        }
        default:
            return block;
    }
}

function childBlocksOf(block: LomdaV3Block): LomdaV3Block[] {
    switch (block.type) {
        case "tabs":
            return block.data.items.flatMap((i) => i.blocks ?? []);
        case "steps":
            return block.data.items.flatMap((i) => i.blocks ?? []);
        case "columns":
            return block.data.columns.flatMap((c) => c.blocks);
        default:
            return [];
    }
}

export function collectLomdaMedia(content: LomdaV3Content): MediaRef[] {
    const refs: MediaRef[] = [];
    for (const page of content.pages) {
        const walk = (blocks: LomdaV3Block[]) => {
            for (const block of blocks) {
                const usage = {
                    pageId: page.id,
                    blockId: block.id,
                    blockType: block.type,
                    variantLabelKey: variantLabelKey(block),
                };
                for (const ref of refsOfBlock(block))
                    refs.push({ ...ref, ...usage });
                walk(childBlocksOf(block));
            }
        };
        walk(page.blocks);
    }
    return refs;
}

function fileNameOf(url: string): string {
    const last = url.split(/[?#]/)[0].split("/").pop() ?? "";
    try {
        return decodeURIComponent(last);
    } catch {
        return last;
    }
}

/** Uploads and icons newest first, then other media in use. */
export function buildMediaLibrary(
    content: LomdaV3Content,
    uploads: LomdaV3Asset[],
    icons: string[],
    deleted: Record<string, string> = {}
): LomdaLibraryItem[] {
    const iconSet = new Set(icons);
    const items = new Map<string, LomdaLibraryItem>();
    const add = (ref: MediaFile): LomdaLibraryItem => {
        const key = ref.videoId ? `video:${ref.videoId}` : ref.url;
        const found = items.get(key);
        if (found) {
            // An image attached as a document is still an image.
            if (found.type === "file") found.type = ref.type;
            found.size ??= ref.size;
            return found;
        }
        const item: LomdaLibraryItem = {
            key,
            type: ref.type,
            url: ref.url,
            videoId: ref.videoId,
            thumbnailUrl: ref.thumbnailUrl,
            name: ref.name || fileNameOf(ref.url),
            size: ref.size,
            isIcon: iconSet.has(ref.url),
            usages: [],
        };
        items.set(key, item);
        return item;
    };

    uploads
        .filter((a) => deleted[a.url] !== a.uploadedAt)
        .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt))
        .forEach((a) =>
            add({ type: a.type, url: a.url, name: a.name, size: a.size })
        );
    [...icons]
        .reverse()
        .forEach((url) => add({ type: "image", url, name: "" }));
    for (const ref of collectLomdaMedia(content)) {
        const item = add(ref);
        if (!item.usages.some((u) => u.blockId === ref.blockId))
            item.usages.push({
                pageId: ref.pageId,
                blockId: ref.blockId,
                blockType: ref.blockType,
                variantLabelKey: ref.variantLabelKey,
            });
    }
    return Array.from(items.values());
}

/** `fallback`: the slot's own kind, for a file the browser gave no MIME
 *  (an .m4a dropped into an audio slot). */
export const assetTypeOfMime = (
    mime = "",
    fallback: LomdaV3AssetType = "file"
): LomdaV3AssetType =>
    mime.startsWith("image/")
        ? "image"
        : mime.startsWith("video/")
          ? "video"
          : mime.startsWith("audio/")
            ? "audio"
            : fallback;

function mediaBlock(
    kind: MediaKind,
    cell: Partial<MediaCell>,
    data: Partial<MediaLayoutBlock["data"]> = {}
): LomdaV3Block {
    const block = createMediaLayout(kind, "none", 1) as MediaLayoutBlock;
    return {
        ...block,
        data: {
            ...block.data,
            ...data,
            cells: block.data.cells.map((c) => ({ ...c, ...cell })),
        },
    };
}

export const MEDIA_BLOCK_TYPE: Record<LomdaV3AssetType, LomdaV3BlockType> = {
    image: "media-layout",
    video: "media-layout",
    audio: "audio",
    file: "attachment",
};

export function createBlockForMedia(item: LomdaLibraryItem): LomdaV3Block {
    const { url, name } = item;
    switch (item.type) {
        case "image":
            return mediaBlock("image", { url, alt: name });
        case "video":
            return mediaBlock("video", {
                url,
                videoId: item.videoId ?? "",
                thumbnailUrl: item.thumbnailUrl ?? "",
                name,
            });
        case "audio": {
            const block = createAudioBlock() as AudioBlock;
            return { ...block, data: { ...block.data, url, name } };
        }
        case "file": {
            const block = createAttachmentBlock() as AttachmentBlock;
            return {
                ...block,
                data: { ...block.data, url, name, size: item.size ?? 0 },
            };
        }
    }
}
